import grey from '@material-ui/core/colors/grey';
import { defaultsDeep } from 'lodash';
import { LUMA_LOGO } from 'containers/App/constants';

const getPropsLuma = (theme, defaults) =>
  defaultsDeep(
    {},
    {
      themeName: 'Luma',
      overrides: {
        MuiTypography: {
          colorPrimary: {
            color: theme.palette.text.primary,
          },
          body2: {
            color: theme.palette.text.primary,
          },
        },
        MuiButtonBase: {
          root: {
            // WORKAROUND CardActionArea is overriding the font
            fontFamily: theme.typography.fontFamily,
          },
        },
        MuiButton: {
          root: {
            borderRadius: 0,
          },
          contained: {
            backgroundColor: theme.palette.common.white,
          },
          containedSecondary: {
            color: theme.palette.common.white,
          },
          text: {
            color: theme.custom.palette.colorSupportSteel,
          },
          label: {
            fontWeight: 500,
            textTransform: 'none',
          },
          outlined: {
            border: `1px solid ${theme.custom.palette.colorSupportSteel}`,
          },
          sizeSmall: {
            // sometimes this size can change from 30 30.5 pixels
            height: 30,
          },
        },
        MuiCardHeader: {
          root: {
            [theme.breakpoints.up('sm')]: {
              paddingLeft: theme.spacing.unit * 3,
              paddingRight: theme.spacing.unit * 3,
            },
          },
        },
        MuiCardContent: {
          root: theme.mixins.gutters({
            '&:last-child': {
              paddingBottom: theme.spacing.unit * 2,
            },
          }),
        },
        MuiDialogActions: {
          root: {
            margin: 0,
            padding: `${theme.spacing.unit * 2}px ${theme.spacing.unit * 3}px`,
            backgroundColor: grey[200],
          },
          action: {
            minWidth: 120,
          },
        },
        MuiFormControlLabel: {
          label: {
            color: theme.palette.text.primary,
          },
        },
        MuiFormLabel: {
          root: {
            '&$focused,&$error': {
              color: theme.palette.text.primary,
            },
          },
          error: {},
          focused: {},
        },
        MuiInput: {
          root: {
            padding: 0,
            backgroundColor: theme.palette.common.white,
            border: `1px solid ${theme.custom.palette.colorLightBlue}`,
            transition: theme.transitions.create(['border-color']),
            '& input, select': {
              height: 46,
            },
            '&select': {
              padding: '0px 8px',
            },
            '&$focused': {
              borderColor: theme.custom.palette.colorSupportSteel,
              zIndex: 10,
              '& input::selection, textarea::selection': {
                color: theme.palette.text.primary,
              },
            },
            '&$disabled': {
              color: `${theme.custom.palette.colorSupportSteel} !important`,
            },
          },
          formControl: {
            'label + &': {
              marginTop: `${theme.spacing.unit * 1}px`,
            },
          },
          input: {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.text.primary,
            fontSize: 14,
            fontWeight: 500,
            padding: '0px 16px',
          },
          multiline: {
            padding: 0,
          },
          inputMultiline: {
            lineHeight: '18px',
            padding: 15,
          },
          error: {
            borderColor: theme.palette.error.main,
          },
        },
        MuiInputLabel: {
          formControl: {
            position: 'relative',
          },
          root: {
            fontSize: 16,
            color: theme.palette.text.primary,
          },
        },
        MuiFormHelperText: {
          root: {
            color: theme.custom.palette.colorSupportSteel,
            fontWeight: 500,
          },
        },
        MuiInputAdornment: {
          root: {
            maxHeight: 'initial',
          },
          positionStart: {
            marginRight: 0,
            marginLeft: 14,
          },
          positionEnd: {
            marginRight: theme.spacing.unit,
            marginLeft: theme.spacing.unit,
          },
        },
        MuiSelect: {
          selectMenu: {
            padding: `13px ${theme.spacing.unit * 3.5}px 13px ${theme.spacing.unit * 2}px`,
          },
          select: {
            '&:focus': {
              backgroundColor: 'inherit',
            },
          },
        },
        MuiChip: {
          root: {
            backgroundColor: theme.custom.palette.colorVeryLightGray,
            color: theme.palette.text.primary,
            borderRadius: 4,
            height: 33,
            fontSize: theme.typography.pxToRem(12),
            fontWeight: 500,
          },
          deleteIcon: {
            marginRight: theme.spacing.unit,
          },
        },
        MuiCheckbox: {
          colorPrimary: {
            color: theme.palette.text.primary,
          },
        },
        MuiRadio: {
          colorPrimary: {
            color: theme.palette.text.primary,
          },
        },
        MuiLinearProgress: {
          root: {
            height: 8,
            borderRadius: 8,
          },
          colorPrimary: {
            backgroundColor: theme.custom.palette.colorSupportSteel,
          },
          bar1Determinate: {
            backgroundColor: theme.custom.palette.colorSupportSlate,
          },
          bar1Indeterminate: {
            backgroundColor: theme.custom.palette.colorSupportSlate,
          },
          bar2Indeterminate: {
            backgroundColor: theme.custom.palette.colorSupportSlate,
          },
        },
        MuiPaper: {
          elevation1: {
            boxShadow: '0px 2px 8px 0px rgba(218, 218, 218, 1)',
          },
        },
        MuiListSubheader: {
          sticky: {
            backgroundColor: theme.palette.common.white,
          },
        },
        MuiListItem: {
          button: {
            '&:active': {
              backgroundColor: theme.custom.palette.colorSupportPowder,
            },
            '&:focus': {
              backgroundColor: theme.custom.palette.colorSupportPowder,
            },
            '&:hover': {
              backgroundColor: theme.custom.palette.colorSupportPowder,
            },
          },
          gutters: {
            [theme.breakpoints.up('sm')]: {
              paddingLeft: theme.spacing.unit * 3,
              paddingRight: theme.spacing.unit * 3,
            },
          },
        },
        MuiMenuItem: {
          root: {
            fontWeight: 500,
            '&:hover': {
              backgroundColor: theme.custom.palette.colorSupportPowder,
            },
            '&$selected': {
              backgroundColor: `${theme.custom.palette.colorLightBlue} !important`,
              fontWeight: 600,
            },
          },
        },
        MuiModal: {
          root: {
            zIndex: theme.zIndex.tooltip,
          },
        },
        MuiPrivateTextarea: {
          root: {
            display: 'flex',
          },
        },
        MuiButtonGroup: {
          grouped: {
            borderRadius: 4,
          },
          groupedContainedPrimary: {
            '&:not(:last-child)': {
              borderColor: theme.palette.common.white,
            },
          },
        },
        // Luma components (don't remove, some of them are used for patient app custom styles)
        LumaHeader: {
          root: {
            backgroundColor: theme.palette.common.white,
          },
          logo: {
            content: `url(${LUMA_LOGO})`,
            maxWidth: 140,
          },
        },
        LumaDialogTitle: {
          root: {
            background: theme.palette.text.primary,
            color: theme.palette.common.white,
            '& > h6': {
              color: 'inherit',
            },
          },
        },
        LumaDialogContent: {
          root: {
            paddingTop: theme.spacing.unit * 3,
          },
        },
        LumaDialogActions: {
          root: {
            margin: 0,
            padding: `${theme.spacing.unit * 2}px ${theme.spacing.unit * 3}px`,
            backgroundColor: grey[200],
          },
          action: {
            minWidth: 120,
          },
        },
        LumaStepper: {
          root: {
            background: 'transparent',
          },
        },
      },
      props: {
        MuiInput: {
          disableUnderline: true,
        },
      },
      // TODO: get rid of custom section
      custom: {
        MuiInput: {
          disableUnderline: true,
        },
      },
    },
    defaults,
  );

export default getPropsLuma;
